import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessagesSquare, Trash2, RefreshCw, Loader2, Mail, Clock, X, UserPlus, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Painting", "Framing", "Drywall", "Flooring", "FRP Panels",
  "Finish Carpentry", "New Construction", "Remodeling", "Maintenance Dept.", "Other",
];

const PROJECT_TYPES = [
  { value: "residential", label: "Residential" },
  { value: "commercial", label: "Commercial" },
  { value: "maintenance", label: "Maintenance" },
  { value: "other", label: "Other" },
];

export default function AdminChats({ token }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/chats`, {
        headers: { "X-Admin-Token": token },
      });
      setSessions(data);
    } catch {
      toast.error("Failed to load chat history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSessions(); }, []);

  const openSession = async (id) => {
    setLoadingDetail(true);
    try {
      const { data } = await axios.get(`${API}/admin/chats/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setSelected(data);
    } catch {
      toast.error("Failed to load conversation.");
    } finally {
      setLoadingDetail(false);
    }
  };

  const remove = async (id, e) => {
    e?.stopPropagation();
    if (!window.confirm("Delete this conversation? Cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/chats/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setSessions((list) => list.filter((s) => s.id !== id));
      if (selected?.id === id) setSelected(null);
      toast.success("Conversation deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="admin-chats" className="space-y-5">
      <div className="bg-white border border-border rounded-md p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 grid place-items-center bg-navy text-white rounded-md">
            <MessagesSquare className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg text-navy">Chat History</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Every conversation visitors had with the assistant. Tap a row to read the transcript.
            </p>
          </div>
        </div>
        <Button onClick={fetchSessions} variant="outline" className="rounded-md" data-testid="chats-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="bg-white border border-border rounded-md p-12 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 mx-auto animate-spin" />
        </div>
      ) : sessions.length === 0 ? (
        <div className="bg-white border border-border rounded-md p-12 text-center text-muted-foreground">
          <MessagesSquare className="w-8 h-8 mx-auto opacity-60 mb-2" />
          No conversations yet. They&apos;ll appear here as soon as visitors chat with the assistant.
        </div>
      ) : (
        <div className="bg-white border border-border rounded-md overflow-hidden divide-y divide-border">
          {sessions.map((s) => (
            <button
              key={s.id}
              onClick={() => openSession(s.id)}
              data-testid={`chat-row-${s.id}`}
              className="w-full text-left p-4 hover:bg-secondary/40 transition-colors flex items-start justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-navy line-clamp-1">{s.label || "Untitled conversation"}</div>
                <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(s.last_message_at)}</span>
                  <span>{s.turn_count} visitor turn{s.turn_count === 1 ? "" : "s"}</span>
                  {s.email_sent && (
                    <Badge className="rounded-md bg-emerald-100 text-emerald-700 font-medium uppercase tracking-wider text-[10px]">
                      <Mail className="w-3 h-3 mr-1" /> Emailed
                    </Badge>
                  )}
                </div>
              </div>
              <Trash2
                className="w-4 h-4 text-muted-foreground hover:text-destructive shrink-0"
                onClick={(e) => remove(s.id, e)}
                role="button"
                aria-label="Delete conversation"
                data-testid={`chat-delete-${s.id}`}
              />
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl rounded-md" data-testid="chat-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-navy">Conversation transcript</DialogTitle>
            <DialogDescription>
              Started {selected ? formatDate(selected.first_seen) : ""} · {selected?.turn_count || 0} visitor turn{selected?.turn_count === 1 ? "" : "s"}
            </DialogDescription>
          </DialogHeader>
          {loadingDetail ? (
            <div className="py-12 text-center text-muted-foreground">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" />
            </div>
          ) : (
            <div className="max-h-[60vh] overflow-y-auto space-y-3 py-2 pr-2">
              {(selected?.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                      m.role === "user"
                        ? "bg-sunrise text-white rounded-br-md"
                        : "bg-secondary border border-border text-navy rounded-bl-md"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex justify-between items-center pt-3 gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {selected?.converted_lead_id && (
                <Badge className="rounded-md bg-emerald-100 text-emerald-700 text-[10px] uppercase tracking-wider">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> Converted to lead
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!selected?.converted_lead_id && (
                <Button
                  onClick={() => setConvertOpen(true)}
                  className="bg-sunrise hover:bg-sunrise-hover rounded-md"
                  data-testid="chat-convert-btn"
                >
                  <UserPlus className="w-4 h-4 mr-1" /> Convert to Lead
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelected(null)} className="rounded-md" data-testid="chat-detail-close">
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConvertDialog
        open={convertOpen}
        setOpen={setConvertOpen}
        session={selected}
        token={token}
        onConverted={(updatedSession) => {
          setSelected(updatedSession);
          // Update list in place — show "emailed" indicator stays, plus we can refetch
          fetchSessions();
        }}
      />
    </div>
  );
}

function ConvertDialog({ open, setOpen, session, token, onConverted }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "Other",
    project_type: "residential",
    delete_chat: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && session) {
      // Try to auto-extract from the transcript
      const transcript = (session.messages || []).map((m) => m.content).join(" ");
      const emailMatch = transcript.match(/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i);
      const phoneMatch = transcript.match(/(\+?\d[\d\s().-]{7,}\d)/);
      setForm({
        name: "",
        email: emailMatch ? emailMatch[0] : "",
        phone: phoneMatch ? phoneMatch[0].trim() : "",
        service_type: "Other",
        project_type: "residential",
        delete_chat: false,
      });
    }
  }, [open, session]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Name, email and phone are required.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/chats/${session.id}/convert`,
        form,
        { headers: { "X-Admin-Token": token } },
      );
      toast.success("Lead created. Find it in the Leads tab.");
      setOpen(false);
      onConverted?.({ ...session, converted_lead_id: "new", converted_at: new Date().toISOString() });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Conversion failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg rounded-md" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Convert Chat to Lead</DialogTitle>
          <DialogDescription>
            We&apos;ll create a new Lead row using the visitor&apos;s contact info; the full transcript is
            copied into the lead message. We tried to auto-detect their email and phone — please verify
            before saving.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy">Name *</span>
              <Input
                data-testid="convert-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="Visitor name"
              />
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy">Phone *</span>
              <Input
                data-testid="convert-phone"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="801-555-0123"
              />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-bold uppercase tracking-wider text-navy">Email *</span>
            <Input
              data-testid="convert-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="mt-1 rounded-md"
              placeholder="visitor@example.com"
            />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy">Service</span>
              <Select value={form.service_type} onValueChange={(v) => setForm((f) => ({ ...f, service_type: v }))}>
                <SelectTrigger className="mt-1 rounded-md" data-testid="convert-service">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-navy">Project type</span>
              <Select value={form.project_type} onValueChange={(v) => setForm((f) => ({ ...f, project_type: v }))}>
                <SelectTrigger className="mt-1 rounded-md" data-testid="convert-project">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground pt-1">
            <input
              type="checkbox"
              checked={form.delete_chat}
              onChange={(e) => setForm((f) => ({ ...f, delete_chat: e.target.checked }))}
              data-testid="convert-delete-chat"
            />
            Delete this conversation after creating the lead
          </label>
          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-md">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="bg-sunrise hover:bg-sunrise-hover rounded-md" data-testid="convert-submit">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create Lead
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}
